import React, { useState, useEffect } from 'react';
import { X, Save, Search, Shield, Check, AlertCircle } from 'lucide-react';
import permissionService from 'features/administrativo/services/permissionService';
import { LoadingSpinner } from 'components/common';

const PresetPermissions = ({ preset, onUpdate, onClose }) => {
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [error, setError] = useState(null);

  // Carregar permissões
  useEffect(() => {
    loadPermissions();
  }, []);

  // Carregar permissões do preset
  useEffect(() => {
    if (preset && preset.permissions) {
      setSelectedPermissions(preset.permissions);
    }
  }, [preset]);

  // Filtrar permissões
  useEffect(() => {
    if (searchTerm) {
      const filtered = permissions.filter(permission =>
        (permission.name && String(permission.name).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (permission.description && String(permission.description).toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPermissions(filtered);
    } else {
      setFilteredPermissions(permissions);
    }
  }, [searchTerm, permissions]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionService.getAll();
      
      // A API retorna { success: true, data: [...] }
      if (response.data && response.data.success) {
        setPermissions(response.data.data);
        setFilteredPermissions(response.data.data);
      } else {
        // Fallback para formato antigo
        setPermissions(response.data || []);
        setFilteredPermissions(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      setError('Erro ao carregar permissões');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionName) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionName)) {
        return prev.filter(p => p !== permissionName);
      } else {
        return [...prev, permissionName];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedPermissions(filteredPermissions.map(p => p.name));
  };

  const handleDeselectAll = () => {
    setSelectedPermissions([]);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onUpdate(selectedPermissions);
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      setError('Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  const isPermissionSelected = (permissionName) => {
    return selectedPermissions.includes(permissionName);
  };

  const getPermissionCategory = (permissionName) => {
    const parts = permissionName.split('.');
    return parts[0] || 'outros';
  };

  const groupPermissionsByCategory = (permissions) => {
    const grouped = {};
    permissions.forEach(permission => {
      const category = getPermissionCategory(permission.name);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(permission);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
        <div className="rounded-lg shadow-xl p-8 max-w-md w-full theme-surface border" style={{ borderColor: 'var(--border)' }}>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
            <h3 className="text-lg font-medium theme-text mb-2">Carregando permissões...</h3>
            <p className="text-sm theme-text-muted">Aguarde enquanto carregamos os dados...</p>
          </div>
        </div>
      </div>
    );
  }

  const groupedPermissions = groupPermissionsByCategory(filteredPermissions);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto theme-surface border" style={{ borderColor: 'var(--border)' }}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold theme-text">
                Gerenciar Permissões - {preset?.label}
              </h2>
              <p className="text-sm theme-text-muted">
                Selecione as permissões que este preset deve ter
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors hover:opacity-80"
              style={{ background: 'var(--surface-muted)' }}
            >
              <X className="h-5 w-5 theme-text-muted" />
            </button>
          </div>

          {/* Busca */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 theme-text-muted" />
              <input
                type="text"
                placeholder="Buscar por nome ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface"
                style={{ borderColor: 'var(--border)' }}
              />
            </div>
          </div>

          {/* Controles */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Selecionar Todas
              </button>
              <button
                onClick={handleDeselectAll}
                className="text-sm theme-text-muted hover:opacity-80 font-medium"
              >
                Desmarcar Todas
              </button>
            </div>
            <div className="text-sm theme-text-muted">
              {selectedPermissions.length} de {permissions.length} permissões selecionadas
            </div>
          </div>

          {/* Erro */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-600">{error}</span>
            </div>
          )}

          {/* Lista de Permissões */}
          <div className="space-y-6">
            {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
              <div key={category}>
                <h3 className="text-lg font-medium theme-text mb-3 capitalize">
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {categoryPermissions.map(permission => (
                    <label
                      key={permission.id}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${
                        isPermissionSelected(permission.name)
                          ? 'bg-blue-500/10 border-blue-400/40'
                          : 'theme-surface border-[color:var(--border)] hover:opacity-90'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isPermissionSelected(permission.name)}
                        onChange={() => handlePermissionToggle(permission.name)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 theme-text-muted" />
                          <span className="text-sm font-medium theme-text">
                            {permission.name}
                          </span>
                          {isPermissionSelected(permission.name) && (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        {permission.description && (
                          <p className="text-xs theme-text-muted mt-1">
                            {permission.description}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-6 border-t mt-6" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg transition-colors theme-text"
              style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Salvar Permissões
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresetPermissions;
